import type { ReactNode } from 'react';
import { Button } from '@/kit/Button';
import { Icon } from '@/kit/Icon';
import { InfoHint } from '@/kit/InfoHint';
import { PageHeader } from '@/kit/PageHeader';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import './StickyReportSettingsFrame.scss';

/** The note every Report Settings page used to show as a blue banner; design review 2 Nov 2026: one info icon instead. */
export const REPORT_SETTINGS_NOTE =
  'Changes here apply to new reports and to any report you regenerate. Existing reports keep the settings they were made with.';

interface Props {
  title: string;
  hostClassName?: string;
  onClose: () => void;
  /** Header actions: 'view' shows Edit, 'edit' shows Cancel and Save, undefined shows only the close button and `actions`. */
  mode?: 'view' | 'edit';
  canEdit?: boolean;
  busy?: boolean;
  onEdit?: () => void;
  onSave?: () => void;
  onCancel?: () => void;
  /** Always-editable page: whether the form differs from what was saved (shows Unsaved changes, Discard and Save). */
  dirty?: boolean;
  onDiscard?: () => void;
  canSave?: boolean;
  /** Extra header action(s), e.g. a "Reset to default" link, shown before the buttons. */
  actions?: ReactNode;
  /** Text of the info icon; the shared Report Settings note when omitted. */
  info?: string;
  children: ReactNode;
}

/**
 * Same layout as `ReportSettingsFrame` (the settings menu, then `sidenav-details`), with the design-review header: the
 * title row, the page's Edit / Save / Cancel and the close button in one sticky bar at the top of the scrolling area,
 * and the long blue banner replaced by an info icon.
 */
export function StickyReportSettingsFrame({
  title,
  hostClassName = '',
  onClose,
  mode,
  canEdit,
  busy,
  onEdit,
  onSave,
  onCancel,
  dirty,
  onDiscard,
  canSave,
  actions,
  info = REPORT_SETTINGS_NOTE,
  children,
}: Props) {
  return (
    <SideMenuPage menu="settings">
      <div className={`sidenav-details ${hostClassName}`.trim()}>
        <PageHeader
          title={title}
          mode={mode}
          canEdit={canEdit}
          canSave={canSave}
          busy={busy}
          dirty={dirty}
          onEdit={onEdit}
          onSave={onSave}
          onCancel={onCancel}
          onDiscard={onDiscard}
        >
          <InfoHint text={info} />
          {actions}
          <Button className="close-btn" aria-label="Close" onClick={onClose}>
            <Icon name="close" />
          </Button>
        </PageHeader>
        {children}
      </div>
    </SideMenuPage>
  );
}
